import {Component, computed, ElementRef, input, Signal, viewChild} from '@angular/core';
import {BaseChartDirective} from 'ng2-charts';
import {CategoryScale, ChartData, TooltipItem} from 'chart.js';
import ChartDataLabels, {Context} from 'chartjs-plugin-datalabels';

import {ChartTools} from '@selpia/shared/utils';
import {VarianceChartPoint} from './variance-chart-point.model';
import {Anchor} from 'chartjs-plugin-datalabels/types/options';

@Component({
  selector: 'lib-variance-chart',
  standalone: true,
  templateUrl: './variance-chart.component.html',
  styleUrls: ['./variance-chart.component.scss'],
  imports: [
    BaseChartDirective,
  ],
  host: {
    '[style.height]': 'chartHeight()',
  },
})
export class VarianceChartComponent {
  #BAR_HEIGHT = 18;
  #VARIANCE_HEIGHT = 8;
  #BAR_GAP = 6;
  #BAR_PADDING = 30;

  canvas = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

  readonly varianceData = input.required<VarianceChartPoint[]>();
  readonly valueFormatter = input<((value: number) => string)>();
  readonly isAligned = input<boolean>(true);

  protected readonly chartHeight: Signal<string> = computed(() => {
    const numberOfEntries = this.varianceData().length;
    const height = numberOfEntries * (this.#BAR_HEIGHT + this.#BAR_GAP) - this.#BAR_GAP + this.#BAR_PADDING;

    return `${height}px`;
  });

  protected readonly chartData: Signal<ChartData<'bar'>> = computed(() => {
    const varianceData = this.varianceData();
    const isAligned = this.isAligned();
    const typographyBlack = this.#getCSS('--typography-black');
    const mainBarColor = this.#getCSS('--main-bar-color');
    const negativeBorder = this.#getCSS('--negative-bar-border');
    const cssPositive = this.#getCSS('--positive-bar-color');
    const cssNegative = this.#getCSS('--negative-bar-color');

    const positiveColor = isAligned ? cssPositive : cssNegative;
    const negativeColor = isAligned ? cssNegative : cssPositive;

    const data: ChartData<'bar'> = {
        labels: varianceData.map(
          (point) => (
            point.label
          )),
        datasets: [
          {
            data: varianceData.map(
              (point) => (
                point.current
              )),
            barThickness: this.#BAR_HEIGHT,
            borderWidth: 0,
            backgroundColor: mainBarColor,
            order: 1,
            grouped: false,
            datalabels: {
              align: function (ctx: Context): Anchor {
                const isNegative = varianceData[ctx.dataIndex].current < 0;
                return isNegative ? 'start' : 'end';
              },
              anchor: function (ctx: Context): Anchor {
                const isNegative = varianceData[ctx.dataIndex].current < 0;
                return isNegative ? 'end' : 'start';
              },
              formatter: this.valueFormatter(),
              display: 'auto',
              color: typographyBlack,
            },
          },
          {
            data: varianceData.map(
              (point) => (
                [point.current, point.previous]
              )),
            barThickness: this.#VARIANCE_HEIGHT,
            order: 0,
            borderWidth: 1,
            borderColor: ChartTools.matchToVarianceSign({
              negative: negativeBorder, positive: positiveColor,
            }),
            backgroundColor: ChartTools.matchToVarianceSign({
              negative: negativeColor, positive: positiveColor,
            }),
            datalabels: {
              labels: {
                variance: {
                  clip: false,
                  anchor: function (ctx: Context): Anchor {
                    const isNegative = varianceData[ctx.dataIndex].current < 0;
                    return isNegative ? 'start' : 'end';
                  },
                  align: function (ctx: Context): Anchor {
                    const isNegative = varianceData[ctx.dataIndex].current < 0;
                    return isNegative ? 'start' : 'end';
                  },
                  formatter: function (_, ctx: Context): string {
                    return varianceData[ctx.dataIndex].varianceLabel;
                  },
                },
                cropper: {
                  anchor: function (ctx: Context): Anchor {
                    const isNegative = varianceData[ctx.dataIndex].current < 0;
                    return isNegative ? 'end' : 'start';
                  },
                  align: function (ctx: Context): Anchor {
                    const isNegative = varianceData[ctx.dataIndex].current < 0;
                    return isNegative ? 'start' : 'end';
                  },
                  formatter: () => '',
                  padding: 0,
                },
              },
            },
          },
        ],
      }
    ;
    return data;
  });

  protected readonly options = computed(() => {
    const textColor = this.#getCSS('--text-color');
    const numberFont = this.#getCSS('--number-font');
    const varianceData = this.varianceData();
    const formatter = this.valueFormatter();

    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      layout: {
        padding: {
          right: 50,
          left: 0,
          top: 0,
          bottom: 0,
        },
      },
      scales: {
        y: {
          grid: {display: false},
          border: {display: false},
          ticks: {
            crossAlign: "far",
            color: textColor,
            callback: function (value: number): string {
              const lbl = varianceData[value].label;
              if (lbl.length > 13) {
                return `${lbl.substring(0, 12)}...`;
              }
              return lbl;
            },
          },
          font: {
            weight: 'medium',
          },
          afterFit: (axis: CategoryScale) => axis.width = 112,
        },
        x: {display: false},
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          filter: function (item: any) {
            return item.datasetIndex === 0;
          },
          callbacks: {
            label: function (tooltipItem: TooltipItem<'bar'>): string {
              if (formatter) {
                return formatter(varianceData[tooltipItem.dataIndex].current);
              }
              return tooltipItem.formattedValue;
            },
          },
        },
        datalabels: {
          color: textColor,
          font: {
            family: numberFont,
            weight: 'medium',
          },
        },
      },
    };
  });

  #getCSS(variableName: string): string {
    const canvas = this.canvas();
    if (!canvas) {
      return '';
    }

    return getComputedStyle(canvas.nativeElement)
      .getPropertyValue(variableName) || getComputedStyle(document.body)
      .getPropertyValue(variableName);
  }

  protected readonly plugins = [ChartDataLabels];
}

